import { supabase } from '@/lib/supabase/client';
import type { Ayah } from '@/types/domain';

export type Bookmark = {
  id: string;
  user_id: string;
  ayah_id: Ayah['id'];
  surah_id: number;
  ayah_number: number;
  label: string | null;
  color: string | null;
  created_at: string;
};

export type BookmarkInsert = {
  user_id: string;
  ayah_id: number;
  surah_id: number;
  ayah_number: number;
  label?: string | null;
};

/**
 * List the signed-in user's bookmarks, newest first.
 * RLS scopes the result to the caller.
 */
export async function listBookmarks(): Promise<Bookmark[]> {
  const { data, error } = await supabase
    .from('bookmarks')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Bookmark[];
}

/** Just the ayah_ids — cheap to subscribe to from every AyahLine. */
export async function listBookmarkAyahIds(): Promise<Set<number>> {
  const { data, error } = await supabase.from('bookmarks').select('ayah_id');
  if (error) throw error;
  return new Set((data ?? []).map((row) => row.ayah_id as number));
}

export async function addBookmark(input: BookmarkInsert): Promise<Bookmark> {
  const { data, error } = await supabase
    .from('bookmarks')
    .insert({
      user_id: input.user_id,
      ayah_id: input.ayah_id,
      surah_id: input.surah_id,
      ayah_number: input.ayah_number,
      label: input.label ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as Bookmark;
}

export async function removeBookmarkByAyah(
  userId: string,
  ayahId: number,
): Promise<void> {
  const { error } = await supabase
    .from('bookmarks')
    .delete()
    .eq('user_id', userId)
    .eq('ayah_id', ayahId);
  if (error) throw error;
}

export async function removeBookmarkById(id: string): Promise<void> {
  const { error } = await supabase.from('bookmarks').delete().eq('id', id);
  if (error) throw error;
}
